import { Suspense } from 'react';
import { Link } from '@/app_components/common/Link';
import { getDisclaimer } from '@/app_data/articles/getDisclaimer';
import { getMenu } from '@/app_data/menu/getMenu';
import type { MenuItem } from '@/app_lib/types/Menu';
import { cn } from '@/app_lib/utils/utils';
import { fortuneSocialLinks } from '@/appconstants';

const trackingData = {
  data: {
    // biome-ignore lint/style/useNamingConvention: <external data>
    contentPlacementCD: 'footer',
    eventAction: 'footer links',
    eventCategory: 'navigation',
  },
};

const Section = ({
  className,
  header,
  subSections,
}: {
  className?: string;
  header: string;
  subSections: MenuItem[][];
}) => (
  <div className={cn('flex flex-col space-y-4 truncate', className)}>
    <span className='typography-heading-graphik-condensed-level-6 text-accent-secondary'>{header}</span>
    <div className='flex space-x-4'>
      {subSections.map((subSection: MenuItem[]) => (
        <ul
          className='overflow-hidden flex flex-col space-y-4'
          key={`subsection-${subSection[0]?.id}`}
        >
          {subSection.map((link) => (
            <li
              className='truncate font-graphik-condensed typography-body-graphik-xsmall font-semibold hover:text-accent-primary'
              key={link.id}
            >
              <Link
                href={link.url}
                trackingData={{ data: { ...trackingData.data, eventLabel: link.label } }}
              >
                {link.label}
              </Link>
            </li>
          ))}
        </ul>
      ))}
    </div>
  </div>
);

// TODO: Make these svgs and move them elsewhere
export const SocialIcon = ({ href, icon }: { href: string; icon: string }) => (
  <Link
    className='border rounded-full w-8 h-8 items-center justify-center flex cursor-pointer hover:text-accent-primary hover:border-accent-primary'
    href={href}
    trackingData={{
      data: {
        // biome-ignore lint/style/useNamingConvention: <external data>
        contentPlacementCD: 'footer',
        eventAction: 'social icon',
        eventCategory: 'social platform',
        eventLabel: icon,
      },
    }}
  >
    <span className={cn('icon w-3 h-3', `icon-${icon}`)} />
  </Link>
);

export const Footer = async () => {
  const [
    rankings1,
    rankings2,
    sections1,
    sections2,
    sections3,
    sections4,
    sections5,
    services,
    support,
    about,
    disclaimer,
  ] = await Promise.all([
    getMenu({ slug: 'footer-rankings-1' }),
    getMenu({ slug: 'footer-rankings-2' }),
    getMenu({ slug: 'footer-sections-1' }),
    getMenu({ slug: 'footer-sections-2' }),
    getMenu({ slug: 'footer-sections-3' }),
    getMenu({ slug: 'footer-sections-4' }),
    getMenu({ slug: 'footer-sections-5' }),
    getMenu({ slug: 'footer-commercial-services-1' }),
    getMenu({ slug: 'footer-customer-support-1' }),
    getMenu({ slug: 'footer-about-1' }),
    getDisclaimer(),
  ]);

  return (
    <footer className='footer flex flex-col gap-4 lg:container-ft'>
      <div className='overflow-hidden border-neutral-300 grid gap-y-4 border-y py-6 divide-y md:divide-y-0 md:divide-x md:grid-cols-4 xl:grid-cols-[repeat(5,fit-content(100%))] px-4 md:px-6 xl:px-12'>
        <Section
          className='border-inherit pb-4 md:pb-0 md:col-span-2 xl:pr-4 xl:col-span-1'
          header='Rankings'
          subSections={[rankings1, rankings2]}
        />
        <Section
          className='border-inherit pb-4 md:pl-4 md:pb-0 md:col-span-2 md:border-r-0 xl:pr-4 xl:col-span-1 xl:border-r-1'
          header='Sections'
          subSections={[sections1, sections2, sections3, sections4, sections5]}
        />
        <Section
          className='border-inherit pb-4 xl:pl-4 md:pb-0 xl:pr-4'
          header='Customer Support'
          subSections={[support]}
        />
        <Section
          className='border-inherit pb-4 md:pl-4 md:pb-0 xl:pr-4'
          header='Commercial Services'
          subSections={[services]}
        />
        <Section
          className='border-inherit md:pl-4'
          header='About Us'
          subSections={[about]}
        />
      </div>
      <div className='flex flex-col xl:flex-row-reverse gap-4 pb-4'>
        <ul className='flex items-center justify-center gap-2'>
          {fortuneSocialLinks.map(({ href, id }: { href: string; id: string }) => (
            <li key={href}>
              <SocialIcon
                href={href}
                icon={id}
              />
            </li>
          ))}
        </ul>
        <div className='typography-body-graphik-xsmall text-neutrals-500'>
          <Suspense fallback={null}>
            <span
              className='description-parser contents'
              /** biome-ignore lint/security/noDangerouslySetInnerHtml: We need this so the OneTrust popup can open properly. */
              dangerouslySetInnerHTML={{
                __html: disclaimer.replace('{OneTrust PopUp}', 'javascript:Optanon.ToggleInfoDisplay();'),
              }}
            />
          </Suspense>
        </div>
      </div>
    </footer>
  );
};
